import { useState, useEffect } from "react";
import { X, Save, RotateCcw, Trash2, Clock, Download } from "lucide-react";
import { Button } from "../components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function BackupModal({ menuType, password, onClose, onRestore }) {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const getAuthHeaders = () => ({
    "Authorization": "Basic " + btoa("admin:" + password),
    "Content-Type": "application/json"
  });

  // Load backups
  useEffect(() => {
    loadBackups();
  }, []);

  const loadBackups = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/menu/${menuType}/backups`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setBackups(data.backups || []);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  // Create backup
  const createBackup = async () => {
    setCreating(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/menu/${menuType}/backup`, {
        method: "POST",
        headers: getAuthHeaders()
      });
      if (res.ok) {
        loadBackups();
      }
    } catch (err) {
      alert("Erreur lors de la sauvegarde");
    }
    setCreating(false);
  };

  // Restore backup
  const restoreBackup = async (backupId) => {
    if (!window.confirm("Restaurer cette sauvegarde ? La carte actuelle sera remplacée.")) return;
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/menu/${menuType}/restore/${backupId}`, {
        method: "POST",
        headers: getAuthHeaders()
      });
      if (res.ok) {
        alert("Sauvegarde restaurée !");
        onRestore();
        onClose();
      }
    } catch (err) {
      alert("Erreur lors de la restauration");
    }
  };

  // Delete backup
  const deleteBackup = async (backupId) => {
    if (!window.confirm("Supprimer cette sauvegarde ?")) return;
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/menu/${menuType}/backup/${backupId}`, {
        method: "DELETE",
        headers: getAuthHeaders()
      });
      if (res.ok) {
        loadBackups();
      }
    } catch (err) {
      alert("Erreur lors de la suppression");
    }
  };

  // Format date
  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-white w-full max-w-md rounded-2xl overflow-hidden shadow-xl animate-slide-up"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-neutral-100 px-5 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold font-['Oswald']">Sauvegardes</h3>
            <p className="text-xs text-neutral-500">
              {menuType === "bar" ? "Carte du Bar" : "Carte Sandwicherie"}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Create backup button */}
        <div className="p-4 border-b">
          <Button 
            onClick={createBackup} 
            disabled={creating}
            className="w-full bg-green-600 hover:bg-green-700"
          >
            <Save className="w-4 h-4 mr-2" />
            {creating ? "Sauvegarde en cours..." : "Créer une sauvegarde"}
          </Button>
        </div>

        {/* Backups list */}
        <div className="max-h-80 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-neutral-500">Chargement...</div>
          ) : backups.length === 0 ? (
            <div className="p-8 text-center text-neutral-500">
              <Clock className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p>Aucune sauvegarde</p>
              <p className="text-xs mt-1">Créez votre première sauvegarde</p>
            </div>
          ) : (
            <div className="divide-y">
              {backups.map((backup, idx) => (
                <div key={backup.backup_id} className="p-4 hover:bg-neutral-50 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm flex items-center gap-2">
                      <Clock className="w-4 h-4 text-neutral-400" />
                      {formatDate(backup.created_at)}
                    </p>
                    {idx === 0 && (
                      <span className="text-xs text-green-600 font-medium">Plus récente</span>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => restoreBackup(backup.backup_id)}
                      title="Restaurer"
                      className="h-8 px-2"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteBackup(backup.backup_id)}
                      title="Supprimer"
                      className="h-8 px-2 text-red-500 hover:text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-neutral-50 px-5 py-3 text-xs text-neutral-500 text-center">
          Les 20 dernières sauvegardes sont conservées
        </div>
      </div>
    </div>
  );
}
